import React, { useState, useEffect } from 'react'
import {
    ClockIcon,
    TagIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon,
    SparklesIcon,
    BugAntIcon,
    RocketLaunchIcon,
    ShieldCheckIcon,
    HeartIcon
} from '@heroicons/react/24/outline'
import LoadingSpinner from '../components/LoadingSpinner'

const Changelog = () => {
    const [changelog, setChangelog] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)

    useEffect(() => {
        fetchChangelog()
    }, [])

    const fetchChangelog = async () => {
        try {
            setLoading(true)
            const response = await fetch('/api/changelog')
            if (response.ok) {
                const data = await response.json()
                setChangelog(data)
                setError(null)
            } else {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`)
            }
        } catch (err) {
            console.error('获取更新日志失败:', err)
            setError(`无法加载更新日志: ${err.message}`)
            setChangelog(null)
        } finally {
            setLoading(false)
        }
    }

    // 错误处理
    if (error) {
        return (
            <div className="min-h-screen bg-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <div className="text-red-400 text-xl mb-4">📋 无法加载更新日志</div>
                    <div className="text-gray-400">{error}</div>
                    <button
                        onClick={fetchChangelog}
                        className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                    >
                        重试
                    </button>
                </div>
            </div>
        )
    }

    if (loading) {
        return <LoadingSpinner />
    }

    if (!changelog || !changelog.versions || changelog.versions.length === 0) {
        return (
            <div className="min-h-screen bg-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <div className="text-gray-400 text-xl mb-4">📋 暂无更新日志</div>
                </div>
            </div>
        )
    }

    const getTypeIcon = (type) => {
        switch (type) {
            case 'feature':
                return <SparklesIcon className="h-5 w-5 text-blue-400" />
            case 'bugfix':
                return <BugAntIcon className="h-5 w-5 text-red-400" />
            case 'improvement':
                return <RocketLaunchIcon className="h-5 w-5 text-green-400" />
            case 'security':
                return <ShieldCheckIcon className="h-5 w-5 text-yellow-400" />
            case 'thanks':
                return <HeartIcon className="h-5 w-5 text-pink-400" />
            default:
                return <TagIcon className="h-5 w-5 text-gray-400" />
        }
    }

    const getTypeColor = (type) => {
        switch (type) {
            case 'feature':
                return 'from-blue-500 to-blue-600'
            case 'bugfix':
                return 'from-red-500 to-red-600'
            case 'improvement':
                return 'from-green-500 to-green-600'
            case 'security':
                return 'from-yellow-500 to-yellow-600'
            case 'thanks':
                return 'from-pink-500 to-pink-600'
            default:
                return 'from-gray-500 to-gray-600'
        }
    }

    const getChangeTypeColor = (type) => {
        switch (type) {
            case 'feature':
                return 'text-blue-400'
            case 'bugfix':
                return 'text-red-400'
            case 'improvement':
                return 'text-green-400'
            case 'security':
                return 'text-yellow-400'
            case 'thanks':
                return 'text-pink-400'
            default:
                return 'text-gray-400'
        }
    }

    const getChangeTypeName = (type) => {
        switch (type) {
            case 'feature':
                return '新功能'
            case 'bugfix':
                return '修复'
            case 'improvement':
                return '优化'
            case 'security':
                return '安全'
            case 'thanks':
                return '鸣谢'
            default:
                return '其他'
        }
    }

    return (
        <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
            <h1 className="text-3xl font-extrabold text-white mb-8 text-center">
                <ClockIcon className="inline-block h-8 w-8 mr-3 text-blue-400" />
                更新日志
            </h1>

            <div className="relative">
                {/* 时间线连接线 */}
                <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-gray-700 hidden sm:block"></div>

                {changelog.versions.map((version, index) => {
                    const Icon = getTypeIcon(version.type)
                    const bgColor = getTypeColor(version.type)

                    return (
                        <div key={version.version} className="mb-10 flex items-start relative">
                            {/* 时间线图标 */}
                            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-gray-800 to-gray-900 border-2 border-gray-700 flex items-center justify-center z-10 shadow-lg hidden sm:flex">
                                {Icon}
                            </div>

                            {/* 内容卡片 */}
                            <div className="flex-grow sm:ml-8 bg-gray-800 rounded-lg shadow-xl p-6 border border-gray-700 hover:border-blue-500 transition-all duration-300">
                                <div className="flex items-center justify-between mb-4">
                                    <h2 className="text-2xl font-bold text-white">
                                        v{version.version}
                                    </h2>
                                    <span className="text-sm text-gray-400">{version.date}</span>
                                </div>

                                <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium text-white mb-4 bg-gradient-to-r ${bgColor}`}>
                                    {Icon}
                                    <span className="ml-2">{version.title}</span>
                                </div>

                                <ul className="list-disc list-inside text-gray-300 space-y-2">
                                    {version.changes.map((change, idx) => (
                                        <li key={idx} className="flex items-start">
                                            <span className="mr-2 text-blue-400">•</span>
                                            <span>
                                                <span className={`font-medium ${getChangeTypeColor(change.type)}`}>
                                                    {getChangeTypeName(change.type)}:
                                                </span>
                                                <div className="text-gray-300 ml-1">
                                                    <span className="whitespace-pre-line">
                                                        {change.description}
                                                    </span>
                                                    {change.image && (
                                                        <div className="mt-3">
                                                            <img
                                                                src={change.image}
                                                                alt="功能截图"
                                                                className="max-w-full h-auto rounded-lg border border-gray-600 shadow-lg"
                                                                style={{ maxWidth: '600px' }}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    )
                })}
            </div>

            {/* 页脚信息 */}
            <div className="text-center text-gray-500 text-sm mt-12">
                <p>共 {changelog.versions.length} 个版本更新</p>
                <p className="mt-2">
                    最新更新时间: {changelog.versions[0]?.date}
                </p>
            </div>
        </div>
    )
}

export default Changelog